// Boiler Plate Imports
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { update as setBreadCrumbs } from "../../redux-store/breadCrumbsSlice";
import API from "../../api";
import { useNavigate } from "react-router-dom";

// Conatiner Component Imports
import { Button, Card } from "@mui/material";
import BreadCrumbs from "../../components/Navigation/breadcrumbs";
import TableHeader from "../../components/Page/tableHeader";

// Page Component Imports
import MetricBoxes from "../../components/Page/metricBoxes";
import TableSubHeader from "../../components/Page/tableSubHeader";
import { OpenLinkNewWindowIcon } from "../../theme/icons";
import { formatDateTimeStamp } from "../../utils/date";

import DeviceConnectivityTestModal from "./deviceConnectivityTestModal";
import { updateDeviceConnectivityTestModal } from "../../redux-store/uxSlice";

import { TableComponent } from "../../components/Table/dataGridPro";

const LowerCaseButton = styled(Button)`
  text-transform: none !important;
`;

const PaddingWrapper = styled.div`
  padding: 0 15px 10px;
  @media (max-width: 1080px) {
    padding: 0 0px 10px;
  }
`;

const Table = React.memo(TableComponent);

const AdminDevicesContainer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [deviceMetrics, setDeviceMetrics] = useState([]);
  const [reloadData, setReloadData] = useState(false);

  const somModelsList = [
    {
      somModel: "PROD B5-SoM 504s",
      productId: 39819,
      productPath: "prod-boreas-monitoring-b504-39819",
    },
    {
      somModel: "PROD B-SoM 404s",
      productId: 22336,
      productPath: "boreas-monitoring-bsom-22336",
    },
    {
      somModel: "DEV B-SoM 404s",
      productId: 27893,
      productPath: "dev-boreas-monitoring-bsom-27893",
    },
    {
      somModel: "DEV B5-SoM 504s",
      productId: 43555,
      productPath: "dev-b-som-504s-43555",
    },
  ];

  const tableColumns = useMemo(() => {
    return [
      {
        headerName: "Device Name",
        field: "name",
        width: 150,
        valueGetter: (value, row) => {
          return row.name;
        },
        renderCell: ({ row }) => {
          return (
            <LowerCaseButton href={`/admin/devices/${row.id}`} rel="noreferrer">
              {row.name}
            </LowerCaseButton>
          );
        },
      },
      {
        headerName: "Particle Device ID",
        field: "particleDeviceId",
        valueGetter: (value, row) => {
          return row.particleDeviceId;
        },
        renderCell: ({ row }) => {
          let productPath = "";
          const product = somModelsList.find(
            (model) =>
              model.productId === row?.particleDevice?.product_id,
          );
          if (product) {
            productPath = product.productPath;
          }

          if (!productPath) {
            return <>{row?.particleDeviceId}</>;
          }

          return (
            <LowerCaseButton
              target="_blank"
              href={`https://console.particle.io/${productPath}/devices/${row.particleDeviceId}`}
              rel="noreferrer"
            >
              {row.particleDeviceId}
              <OpenLinkNewWindowIcon
                sx={{ fontSize: "16px", marginLeft: "3px" }}
              />
            </LowerCaseButton>
          );
        },
      },
      {
        headerName: "Customer Tank",
        field: "tankName",
        valueGetter: (value, row) => {
          return row.tankName;
        },
        renderCell: ({ row }) => {
          if (row?.tankName) {
            return (
              <LowerCaseButton
                target="_blank"
                href={`/${row?.tankOrganizationId}/tanks/${row?.tankId}`}
                rel="noreferrer"
              >
                {row?.tankName}
                <OpenLinkNewWindowIcon
                  sx={{ fontSize: "16px", marginLeft: "3px" }}
                />
              </LowerCaseButton>
            );
          } else {
            return;
          }
        },
      },
      {
        headerName: "Firmware",
        field: "fwVersion",
        type: "number",
        valueGetter: (value, row) => {
          return row.fwVersion;
        },
        renderCell: ({ row }) => {
          if (!row?.fwVersion) {
            return <></>;
          }
          return <>v{row.fwVersion}</>;
        },
      },
      {
        headerName: "Quality Certificate",
        field: "qcIsValid",
        valueGetter: (value, row) => {
          return row.qcIsValid;
        },
        renderCell: ({ row }) => {
          if (row?.qcIsValid) {
            return (
              <Button
                target="_blank"
                href={`devices/${row.id}/qc`}
                rel="noreferrer"
              >
                View QC
                <OpenLinkNewWindowIcon
                  sx={{ fontSize: "16px", marginLeft: "3px" }}
                />
              </Button>
            );
          } else {
            return (
              <Button
                target="_blank"
                disabled
                href={`devices/${row.id}/qc`}
                rel="noreferrer"
              >
                View QC
                <OpenLinkNewWindowIcon
                  sx={{ fontSize: "16px", marginLeft: "3px" }}
                />
              </Button>
            );
          }
        },
      },
      {
        headerName: "Calibration",
        field: "calibrationStatus",
        valueGetter: (value, row) => {
          return row.qcIsValid;
        },
        renderCell: ({ row }) => {
          switch (row?.calibrationStatus) {
            case "no-cal":
              return (
                <Button
                  target="_blank"
                  href={`devices/${row.id}/calibrate`}
                  rel="noreferrer"
                >
                  Calibrate
                  <OpenLinkNewWindowIcon
                    sx={{ fontSize: "16px", marginLeft: "3px" }}
                  />
                </Button>
              );
            default:
              return row?.calibrationStatus;
          }
        },
      },
      {
        headerName: "Connectivity Test",
        field: "connectivityTestStatus",
        valueGetter: (value, row) => {
          return row.connectivityTestStatus;
        },
        renderCell: ({ row }) => {
          switch (row?.connectivityTestStatus) {
            case "no-test":
              return (
                <Button
                  onClick={() =>
                    handlers.openConnectivityTestModalClick({
                      device: row,
                    })
                  }
                >
                  Start Test
                </Button>
              );
            default:
              return row?.connectivityTestStatus;
          }
        },
      },
      {
        headerName: "Particle SoM Model (Product ID)",
        field: "product",
        valueGetter: (value, row) => {
          return row?.particleDevice?.product || "Unknown Model";
        },
        renderCell: ({ row }) => {
          const product = somModelsList.find(
            (model) =>
              model.productId === row?.particleDevice?.product_id,
          );
          if (!product) {
            return <>Unsupported Model</>;
          }
          return (
            <LowerCaseButton
              target="_blank"
              href={`https://console.particle.io/${product.productPath}/devices`}
              rel="noreferrer"
            >
              {product.somModel} ({product.productId})
              <OpenLinkNewWindowIcon
                sx={{ fontSize: "16px", marginLeft: "3px" }}
              />
            </LowerCaseButton>
          );
        },
      },
      {
        headerName: "Date Created",
        field: "dateCreatedObject",
        type: "dateTime",
      },
    ];
  }, []);

  //Polling Setup
  const [dataInterval, setDataInterval] = useState();
  let refreshIntervalSeconds = 10;
  const [pollingStarted, setPollingStarted] = useState(false);
  const [nextRefreshInSeconds, setNextRefreshInSeconds] = useState(
    refreshIntervalSeconds,
  );

  useEffect(() => {
    dispatch(
      setBreadCrumbs([
        {
          title: "Admin Devices",
          link: "/admin/devices",
        },
      ]),
    );
    setDeviceMetrics([
      {
        title: "TOTAL DEVICES",
        value: "--",
      },
    ]);
  }, []);

  useEffect(() => {
    if (!pollingStarted) {
      PollData();
      setPollingStarted(true);
    }
    //Clear time on unmount
    return () => {
      setPollingStarted(false);
      clearInterval(dataInterval);
    };
  }, []);

  useEffect(() => {
    if (nextRefreshInSeconds === 0) {
      FetchData();
      setNextRefreshInSeconds(refreshIntervalSeconds);
    }
  }, [nextRefreshInSeconds]);

  const PollData = async () => {
    clearInterval(dataInterval);
    const d = setInterval(() => {
      setNextRefreshInSeconds((s) => s - 1);
    }, 1000);
    setDataInterval(d);
  };

  const FetchData = async () => {
    setReloadData(true);
  };
  //END Polling

  const dataLayer = {
    GetAllDevices: async () => {
      const devices = await API.Device.DeviceGetAll();

      setDeviceMetrics([
        {
          title: "TOTAL DEVICES",
          value: devices.length,
        },
      ]);

      const devicesRows = devices.map((device) => {
        let calibrationStatus = "no-cal";
        if (
          device.calibrationRecords.length &&
          device.calibrationRecords[0].finishDate
        ) {
          if (device.calibrationRecords[0]?.passed) {
            calibrationStatus = `Completed - ${formatDateTimeStamp(
              device.calibrationRecords[0].finishDate,
            )}`;
          } else if (device.calibrationRecords[0]?.failed) {
            calibrationStatus = `Failed - ${formatDateTimeStamp(
              device.calibrationRecords[0].finishDate,
            )}`;
          }
        } else if (device.calibrationRecords.length) {
          calibrationStatus = `Started - ${formatDateTimeStamp(
            device.calibrationRecords[0].startDate,
          )}`;
        }

        let connectivityTestStatus = "no-test";
        if (
          device.connectivityTestRecords.length &&
          device.connectivityTestRecords[0].finishDate
        ) {
          if (device.connectivityTestRecords[0]?.passed === true) {
            connectivityTestStatus = `Completed - ${formatDateTimeStamp(
              device.connectivityTestRecords[0].finishDate,
            )}`;
          } else if (device.connectivityTestRecords[0]?.passed === false) {
            connectivityTestStatus = `Failed - ${formatDateTimeStamp(
              device.connectivityTestRecords[0].finishDate,
            )}`;
          }
        } else if (device.connectivityTestRecords.length) {
          connectivityTestStatus = `Started - ${formatDateTimeStamp(
            device.connectivityTestRecords[0].startDate,
          )}`;
        }

        return {
          ...device,
          tankId: device?.tank?.id || "",
          tankName: device?.tank?.name || "",
          tankOrganizationId: device?.tank?.organizationId || "",
          connectivityTestStatus,
          calibrationStatus,
          fwVersion: device.particleDevice?.firmware_version,
          dateCreatedObject: new Date(device.created),
        };
      });
      devicesRows.sort((a, b) => {
        return b.name - a.name;
      });
      setReloadData(false);
      return devicesRows;
    },
  };

  const handlers = {
    addDeviceClick: () => {
      navigate("add");
    },
    openConnectivityTestModalClick: async ({ device }) => {
      dispatch(
        updateDeviceConnectivityTestModal({
          show: true,
          device,
        }),
      );
    },
  };

  return (
    <>
      <MetricBoxes title={""} metrics={deviceMetrics} />
      <PaddingWrapper>
        <Card>
          <TableHeader>
            <div>
              <h2>Devices</h2>
              <TableSubHeader>
                View all devices in the Boreas system below
              </TableSubHeader>
            </div>

            <Button
              onClick={handlers.addDeviceClick}
              variant="contained"
              color="primary"
              size="large"
            >
              ADD DEVICES
            </Button>
          </TableHeader>
          <Table
            columns={tableColumns}
            label={""}
            isAdminView={true}
            reloadData={reloadData}
            placeholder={"There are no devices in the system yet"}
            getRowsFunction={dataLayer.GetAllDevices}
            initialState={{
              pinnedColumns: { left: ["name"] },
              sorting: {
                sortModel: [{ field: "dateCreatedObject", sort: "desc" }],
              },
            }}
          />
        </Card>
      </PaddingWrapper>
      <DeviceConnectivityTestModal />
    </>
  );
};

export default AdminDevicesContainer;
